import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/services/api";
import DashboardLayout from "@/components/layout/DashboardLayout";
import StatusBadge from "@/components/common/StatusBadge";
import InlineDocViewer from "@/components/common/InlineDocViewer";
import CertificateDownload from "@/components/common/CertificateDownload";
import InlineCertificateViewer from "@/components/common/InlineCertificateViewer";
import { LetterDownload, InlineLetterViewer } from "@/components/common/LetterOfGoodStanding";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Combobox } from "@/components/ui/combobox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useLocationData } from "@/lib/locations";
import { useCourses } from "@/hooks/use-courses";
import { emailMembershipDocs } from "@/lib/membershipDocs";
import {
  DOCUMENT_SECTIONS,
  DOCUMENT_TYPE_OPTIONS,
  REQUIRED_DOCUMENT_SLOTS,
} from "@/constants/documents";
import ExamResultDetail from "@/components/common/ExamResultDetail";
import { ArrowLeft, Mail, Phone, Calendar, FileText, Upload, Send, Download, CheckCircle, Pencil, Plus, Trash2, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import { toast } from "@/hooks/use-toast";
import { memberNumberOf } from "@/lib/membershipNumber";

// Supabase Storage rejects object keys with spaces, colons and other characters
// (e.g. "Screenshot 2026-07-03 at 11.29.52 AM.png" → "Invalid key"). Make the name safe.
const sanitizeFileName = (name: string) => name.replace(/[^a-zA-Z0-9.\-_]/g, "_").replace(/_+/g, "_");

const STATUS_FLOW = [
  "pending", "document_verified", "course_assigned", "eligible_for_exam", "exception", "sent_for_approval",
] as const;

const STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  document_verified: "Documents Verified",
  course_assigned: "Course Assigned",
  eligible_for_exam: "Eligible for Exam",
  exception: "Exemption",
  sent_for_approval: "Sent for Approval",
};

const StudentDetail = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [profile, setProfile] = useState<any>(null);
  const [application, setApplication] = useState<any>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [examAssignments, setExamAssignments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusLoading, setStatusLoading] = useState(false);
  const [markPaidLoading, setMarkPaidLoading] = useState(false);
  const [reassigningId, setReassigningId] = useState<string | null>(null);
  const [applyForm, setApplyForm] = useState({ qualification: "", notes: "", city: "", country: "", admission_year: "", gender: "" });
  const loc = useLocationData();
  const applyCityOpts = useMemo(() => loc?.citiesForCountry(applyForm.country) ?? [], [loc, applyForm.country]);
  const { courses } = useCourses();
  const courseOpts = useMemo(() => courses.map((c) => ({ value: c.name, label: c.name })), [courses]);
  const currentYear = new Date().getFullYear();
  const yearOpts = useMemo(() => Array.from({ length: currentYear - 1990 + 1 }, (_, i) => currentYear - i), [currentYear]);
  const [applyFiles, setApplyFiles] = useState<{ file: File; slotId: string; type: string }[]>([]);
  const [applyLoading, setApplyLoading] = useState(false);
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [newDocType, setNewDocType] = useState(DOCUMENT_TYPE_OPTIONS[0]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docToDelete, setDocToDelete] = useState<any>(null);
  const [deletingDoc, setDeletingDoc] = useState(false);

  const fetchExamAssignments = async (appId: string) => {
    try {
      const res = await api.get(`/think-exam/results/${appId}`);
      setExamAssignments(res.data?.assignments || []);
    } catch {
      setExamAssignments([]);
    }
  };

  const fetchData = async () => {
    if (!userId) return;
    const [{ data: prof }, { data: apps }] = await Promise.all([
      supabase.from("profiles").select("*").eq("user_id", userId).maybeSingle(),
      supabase.from("applications").select("*").eq("user_id", userId).order("created_at", { ascending: false }).limit(1),
    ]);
    setProfile(prof);
    const app = apps?.[0] || null;
    setApplication(app);
    if (app) {
      const { data: docs } = await supabase.from("documents").select("*").eq("application_id", app.id);
      setDocuments(docs || []);
      await fetchExamAssignments(app.id);
    } else if (userId) {
      // No application yet — load exam assignments by user_id directly
      const { data: rows } = await supabase.from("exam_assignments").select("*").eq("user_id", userId).order("created_at", { ascending: true });
      setExamAssignments(rows || []);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pull-on-load: when opening a student, ask ThinkExam for any pending results for them.
  useEffect(() => {
    if (!userId) return;
    api.post("/think-exam/sync-pending", { userId }).then((res) => {
      if (res.data?.updated) fetchData();
    }).catch(() => {});
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live updates: when this student's exam result changes (webhook or another sync), refresh.
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`student-exam-${userId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "exam_assignments", filter: `user_id=eq.${userId}` }, () => fetchData())
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const getNextStatuses = (currentStatus: string) => {
    const idx = STATUS_FLOW.indexOf(currentStatus as any);
    if (idx === -1) return [];
    return STATUS_FLOW.slice(idx + 1) as unknown as string[];
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!user || !application) return;
    setStatusLoading(true);
    const { error } = await supabase.from("applications").update({ status: newStatus as any, reviewed_by: user.id }).eq("id", application.id);
    if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); setStatusLoading(false); return; }
    await supabase.from("application_status_history").insert({ application_id: application.id, old_status: application.status, new_status: newStatus as any, changed_by: user.id });
    // Fire the matching status email (e.g. "eligible for exam"). No-op for statuses without an email.
    try { await api.post(`/applications/${application.id}/notify-status`, { status: newStatus }); } catch (_) {}
    toast({ title: "Status updated", description: `Status changed to ${STATUS_LABELS[newStatus] || newStatus}` });
    setStatusLoading(false);
    fetchData();
  };

  const handleSubmitApplication = async () => {
    if (!profile || !user || !userId) return;
    // All fields mandatory except Notes.
    if (!applyForm.qualification || !applyForm.country || !applyForm.city || !applyForm.admission_year || !applyForm.gender) {
      toast({ title: "Missing fields", description: "Qualification, Gender, Country, City / Town and Admission Year are required.", variant: "destructive" });
      return;
    }
    const missingDocs = REQUIRED_DOCUMENT_SLOTS
      .filter((slot) => !applyFiles.some((f) => f.slotId === slot.id))
      .map((slot) => slot.label);
    if (missingDocs.length) {
      toast({ title: "Missing documents", description: `Please upload: ${missingDocs.join(", ")}.`, variant: "destructive" });
      return;
    }
    setApplyLoading(true);
    const { data: app, error } = await supabase.from("applications").insert({
      user_id: userId,
      full_name: profile.full_name,
      email: profile.email,
      phone: profile.phone || "",
      qualification: applyForm.qualification,
      notes: applyForm.notes,
      city: applyForm.city || null,
      country: applyForm.country || null,
      admission_year: applyForm.admission_year ? parseInt(applyForm.admission_year) : null,
      gender: applyForm.gender || null,
    }).select().single();
    if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); setApplyLoading(false); return; }
    const uploadedPaths: string[] = [];
    for (const { file, type } of applyFiles) {
      const filePath = `${userId}/${app.id}/${Date.now()}_${sanitizeFileName(file.name)}`;
      const { error: uploadError } = await supabase.storage.from("application-documents").upload(filePath, file);
      if (uploadError) {
        // A required document failed — roll back so we never leave a document-less application.
        if (uploadedPaths.length) await supabase.storage.from("application-documents").remove(uploadedPaths);
        await supabase.from("documents").delete().eq("application_id", app.id);
        await supabase.from("applications").delete().eq("id", app.id);
        toast({ title: "Upload Failed", description: `${type}: ${uploadError.message}`, variant: "destructive" });
        setApplyLoading(false);
        return;
      }
      uploadedPaths.push(filePath);
      await supabase.from("documents").insert({ application_id: app.id, user_id: userId, document_type: type, file_name: file.name, file_path: filePath });
    }
    // Link any unlinked exam_assignments to the new application
    try { await api.post("/think-exam/link-application", { userId, applicationId: app.id }); } catch (_) {}
    toast({ title: "Application submitted", description: `Application created for ${profile.full_name}` });
    setApplyLoading(false);
    fetchData();
  };

  const handleReassignExam = async (examAssignmentId: string) => {
    if (!application) return;
    setReassigningId(examAssignmentId);
    try {
      await api.post(`/think-exam/reassign/${application.id}`, { examAssignmentId });
      toast({
        title: "Exam reassigned",
        description: "The student can now see the exam again. Next, reset this test for the candidate in the ThinkExam admin panel so their previous attempt is cleared.",
      });
      fetchData();
    } catch (e: any) {
      toast({ title: "Reassign failed", description: e.response?.data?.error || e.message, variant: "destructive" });
    }
    setReassigningId(null);
  };

  const uploadNewDocument = async (file: File, type: string) => {
    if (!file || !application || !user || !userId) return;
    setUploadingDoc(true);
    const filePath = `${userId}/${application.id}/${Date.now()}_${sanitizeFileName(file.name)}`;
    const { error: uploadError } = await supabase.storage.from("application-documents").upload(filePath, file);
    if (uploadError) { toast({ title: "Upload Failed", description: uploadError.message, variant: "destructive" }); setUploadingDoc(false); return; }
    const { error: docError } = await supabase.from("documents").insert({ application_id: application.id, user_id: userId, document_type: type, file_name: file.name, file_path: filePath });
    if (docError) { toast({ title: "Record Failed", description: docError.message, variant: "destructive" }); setUploadingDoc(false); return; }
    toast({ title: "Document added", description: `${type} uploaded successfully.` });
    setUploadingDoc(false);
    setShowAddDoc(false);
    fetchData();
  };

  const deleteDocument = async (doc: any) => {
    setDeletingDoc(true);
    await supabase.storage.from("application-documents").remove([doc.file_path]);
    const { error } = await supabase.from("documents").delete().eq("id", doc.id);
    setDeletingDoc(false);
    if (error) { toast({ title: "Delete Failed", description: error.message, variant: "destructive" }); return; }
    setDocToDelete(null);
    toast({ title: "Document deleted", description: `${doc.document_type} removed.` });
    fetchData();
  };

  const allExamsPassed = examAssignments.length > 0 && examAssignments.every(e => e.status === "passed");
  const hasFailedExams = examAssignments.some(e => e.status === "failed");

  const effectiveStatus = application?.payment_status === "paid" ? "paid" : application?.status;
  const initials = profile?.full_name?.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2) || "?";

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        {/* Back */}
        <button
          onClick={() => navigate("/growlearn/students")}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group"
        >
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
          Back to Students
        </button>

        {loading ? (
          <div className="flex justify-center py-24">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : !profile ? (
          <Card><CardContent className="py-16 text-center text-muted-foreground">Student not found.</CardContent></Card>
        ) : (
          <>
            {/* Profile header */}
            <Card className="glass-premium shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-5">
                  <div className="w-16 h-16 rounded-2xl gradient-warm flex items-center justify-center text-xl font-bold text-white shadow-md shrink-0">
                    {initials}
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-2xl font-bold text-foreground">{profile.full_name}</h2>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5">
                      <div className="flex items-center gap-1.5 text-muted-foreground text-xs">
                        <Mail className="h-3.5 w-3.5" />{profile.email}
                      </div>
                      {profile.phone && (
                        <div className="flex items-center gap-1.5 text-muted-foreground text-xs">
                          <Phone className="h-3.5 w-3.5" />{profile.phone}
                        </div>
                      )}
                      <div className="flex items-center gap-1.5 text-muted-foreground text-xs">
                        <Calendar className="h-3.5 w-3.5" />Registered {format(new Date(profile.created_at), "MMM d, yyyy")}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {application ? (
              <>
                {/* Status strip — full width */}
                <Card className="glass-premium shadow-sm">
                  <CardContent className="px-6 py-5">
                    <div className="flex items-center gap-8">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Status</p>
                        <StatusBadge status={effectiveStatus} />
                      </div>
                      <div className="w-px self-stretch bg-border/50 shrink-0" />
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Payment</p>
                        <span className={`text-sm font-semibold ${application.payment_status === "paid" ? "text-green-600" : "text-muted-foreground"}`}>
                          {application.payment_status === "paid" ? "Paid" : "Unpaid"}
                        </span>
                      </div>
                      <div className="w-px self-stretch bg-border/50 shrink-0" />
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">App ID</p>
                        <p className="font-mono text-sm font-semibold text-foreground">{application.id.slice(0, 8)}</p>
                      </div>
                      <div className="w-px self-stretch bg-border/50 shrink-0" />
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Submitted</p>
                        <p className="text-sm font-semibold text-foreground">{format(new Date(application.created_at), "MMM d, yyyy")}</p>
                      </div>
                      {(application.payment_status === "paid" || application.status === "certified") && (
                        <>
                          <div className="w-px self-stretch bg-border/50 shrink-0 ml-auto" />
                          <div className="shrink-0">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Certificate &amp; Letter</p>
                            <div className="flex items-center gap-1.5">
                              <InlineCertificateViewer
                                fullName={profile.full_name}
                                certId={memberNumberOf(application)}
                                date={format(new Date(application.updated_at), "MMMM d, yyyy")}
                              />
                              <CertificateDownload
                                fullName={profile.full_name}
                                certId={memberNumberOf(application)}
                                date={format(new Date(application.updated_at), "MMMM d, yyyy")}
                                applicationId={application.id}
                              />
                              <InlineLetterViewer
                                fullName={profile.full_name}
                                memberNum={memberNumberOf(application)}
                                date={format(new Date(application.updated_at), "MMMM d, yyyy")}
                                city={application.city}
                                country={application.country}
                                gender={application.gender}
                              />
                              <LetterDownload
                                fullName={profile.full_name}
                                memberNum={memberNumberOf(application)}
                                date={format(new Date(application.updated_at), "MMMM d, yyyy")}
                                city={application.city}
                                country={application.country}
                                gender={application.gender}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Two-column grid: Member Details | Documents */}
                <div className="grid grid-cols-3 gap-6">
                  <div className="col-span-2 flex flex-col gap-6">
                  {/* Member Details */}
                  <Card className="glass-premium shadow-sm flex-1">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-5">
                        <h3 className="text-sm font-bold text-foreground border-l-[3px] border-primary pl-3">Member Details</h3>
                        <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs gap-1.5" onClick={() => navigate(`/growlearn/students/${userId}/edit`)}>
                          <Pencil className="h-3 w-3" /> Edit
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-0">
                        {[
                          { label: "Qualification", value: application.qualification },
                          { label: "Admission Year", value: application.admission_year },
                          { label: "City / Town", value: application.city },
                          { label: "Country", value: application.country },
                          { label: "Gender", value: application.gender ? application.gender[0].toUpperCase() + application.gender.slice(1) : null },
                        ].map(({ label, value }, i, arr) => (
                          <div key={label} className={`py-4 ${i % 2 === 0 ? "pr-8 border-r border-border/30" : "pl-8"} ${i < arr.length - 2 ? "border-b border-border/30" : ""}`}>
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
                            <p className={`text-sm font-medium ${value ? "text-foreground" : "text-muted-foreground/40"}`}>{value || "—"}</p>
                          </div>
                        ))}
                      </div>
                      {application.notes && (
                        <div className="mt-5 p-4 rounded-lg bg-amber-50 dark:bg-amber-950/20 border-l-[3px] border-amber-400">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-600 mb-1">Notes</p>
                          <p className="text-sm text-foreground">{application.notes}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Actions */}
                  {!["sent_for_approval", "approved", "rejected", "paid", "certified"].includes(application.status) && (
                    <Card className="glass-premium shadow-sm">
                      <CardContent className="p-6">
                        <h3 className="text-sm font-bold text-foreground border-l-[3px] border-primary pl-3 mb-4">Update Status</h3>
                        <Select key={application.status} disabled={statusLoading} onValueChange={handleStatusChange}>
                          <SelectTrigger>
                            <SelectValue placeholder={STATUS_LABELS[application.status] || application.status} />
                          </SelectTrigger>
                          <SelectContent>
                            {getNextStatuses(application.status).map((s) => (
                              <SelectItem key={s} value={s}>{STATUS_LABELS[s] || s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </CardContent>
                    </Card>
                  )}

                  {/* Exam Results Card */}
                  {examAssignments.length > 0 && (
                    <Card className="glass-premium shadow-sm">
                      <CardContent className="p-6">
                        <h3 className="text-sm font-bold text-foreground border-l-[3px] border-primary pl-3 mb-4">Exam Results</h3>
                        <div className="space-y-3">
                          {examAssignments.map((ea: any) => (
                            <div key={ea.id} className={`rounded-xl border p-4 ${ea.status === "passed" ? "border-green-200 bg-green-50/50 dark:bg-green-950/10" : ea.status === "failed" ? "border-red-200 bg-red-50/50 dark:bg-red-950/10" : "border-border/50 bg-muted/10"}`}>
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-semibold text-foreground truncate">{ea.think_exam_test_name || ea.think_exam_test_id}</p>
                                  <p className="text-xs text-muted-foreground mt-0.5">{ea.think_exam_group_name}</p>
                                  {ea.score != null && (
                                    <p className="text-xs mt-1.5 font-medium">
                                      Score: <span className="text-foreground">{ea.score}/{ea.total_marks}</span>
                                      {ea.percentage != null && <span className="ml-1.5 text-muted-foreground">({ea.percentage}%)</span>}
                                    </p>
                                  )}
                                </div>
                                <div className="flex flex-col items-end gap-2 shrink-0">
                                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${ea.status === "passed" ? "bg-green-100 text-green-700" : ea.status === "failed" ? "bg-red-100 text-red-700" : ea.status === "reassigned" ? "bg-amber-100 text-amber-700" : "bg-muted text-muted-foreground"}`}>
                                    {ea.status === "passed" ? "Passed" : ea.status === "failed" ? "Failed" : ea.status === "reassigned" ? "Reassigned" : "Assigned"}
                                  </span>
                                  {ea.status === "failed" && (
                                    <div className="flex gap-1">
                                      <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs text-red-600 border-red-200 hover:bg-red-50" disabled={reassigningId === ea.id} onClick={() => handleReassignExam(ea.id)}>
                                        {reassigningId === ea.id ? "Reassigning..." : "Reassign"}
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                              {(ea.status === "passed" || ea.status === "failed") && <ExamResultDetail exam={ea} />}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Send for Approval — when eligible_for_exam AND all exams passed, OR an
                      Exemption student (no exam required). */}
                  {((application.status === "eligible_for_exam" && allExamsPassed) || application.status === "exception") && (
                    <Button className="w-full gradient-warm text-white border-0 shadow-md h-11" disabled={statusLoading} onClick={() => handleStatusChange("sent_for_approval")}>
                      <Send className="mr-2 h-4 w-4" /> Send for ACA Approval
                    </Button>
                  )}

                  {application.status === "eligible_for_exam" && hasFailedExams && (
                    <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
                      <span className="font-semibold">Exam(s) failed.</span> Reassign failed exams above before sending for approval.
                    </div>
                  )}

                  {application.status === "approved" && application.payment_status !== "paid" && (
                    <Button
                      className="w-full gradient-warm text-white border-0 shadow-md h-11"
                      disabled={markPaidLoading}
                      onClick={async () => {
                        if (!user) return;
                        setMarkPaidLoading(true);
                        // Select the row back: the membership number is allocated by the
                        // database on this update, so the local copy does not have it yet.
                        const { data: certified, error } = await supabase.from("applications").update({
                          payment_status: "paid",
                          payment_date: new Date().toISOString(),
                          payment_reference: "manual_growlearn_" + user.id,
                          status: "certified" as any,
                        }).eq("id", application.id).select().maybeSingle();
                        if (!error) {
                          try {
                            await api.patch(`/applications/${application.id}/status`, { status: "certified" });
                          } catch (_) {}
                          // Auto-email Certificate + Letter of Good Standing on certification.
                          emailMembershipDocs(application.id, {
                            fullName: profile.full_name,
                            certId: memberNumberOf(certified ?? application),
                            date: format(new Date(), "MMMM d, yyyy"),
                            city: application.city,
                            country: application.country,
                            gender: application.gender,
                          }).catch(() => {});
                          toast({ title: "Marked as Paid", description: `Payment recorded for ${profile.full_name}. Documents emailed.` });
                          fetchData();
                        } else {
                          toast({ title: "Error", description: error.message, variant: "destructive" });
                        }
                        setMarkPaidLoading(false);
                      }}
                    >
                      <CheckCircle className="mr-2 h-4 w-4" />
                      {markPaidLoading ? "Processing..." : "Mark as Paid"}
                    </Button>
                  )}
                </div>

                {/* Right column: Documents */}
                <div>
                  <Card className="glass-premium shadow-sm h-full">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-bold text-foreground border-l-[3px] border-primary pl-3">Documents</h3>
                        <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs gap-1.5" onClick={() => setShowAddDoc((v) => !v)}>
                          <Plus className="h-3 w-3" /> Add
                        </Button>
                      </div>
                      {showAddDoc && (
                        <div className="mb-3 p-3 rounded-xl border border-primary/30 bg-primary/5 space-y-2.5">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Add Document</p>
                          <Select value={newDocType} onValueChange={setNewDocType}>
                            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {DOCUMENT_TYPE_OPTIONS.map((t) => (
                                <SelectItem key={t} value={t}>{t}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <div className="flex items-center gap-2">
                            <label className="cursor-pointer flex-1">
                              <input
                                type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" disabled={uploadingDoc}
                                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadNewDocument(f, newDocType); e.target.value = ""; }}
                              />
                              <span className="inline-flex w-full items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-primary/40 text-primary hover:bg-primary hover:text-white hover:border-primary transition-all duration-200">
                                <Upload className="h-3 w-3" /> {uploadingDoc ? "Uploading…" : "Choose file & upload"}
                              </span>
                            </label>
                            <Button size="sm" variant="ghost" className="h-9 px-2 text-xs" onClick={() => setShowAddDoc(false)}>Cancel</Button>
                          </div>
                        </div>
                      )}
                      <div className="space-y-2">
                        {documents.length > 0 ? documents.map((doc) => (
                          <div key={doc.id} className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-muted/10">
                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                              <FileText className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-foreground">{doc.document_type}</p>
                              <p className="text-xs text-muted-foreground truncate">{doc.file_name}</p>
                            </div>
                            <div className="flex gap-1 shrink-0">
                              <InlineDocViewer filePath={doc.file_path} fileName={doc.file_name} />
                              <Button
                                size="sm" variant="outline" title="Download"
                                className="h-8 w-8 p-0 border-border/60 hover:bg-primary hover:text-white hover:border-primary transition-all duration-200"
                                onClick={async () => {
                                  const { data } = await supabase.storage.from("application-documents").createSignedUrl(doc.file_path, 3600, { download: true });
                                  if (data?.signedUrl) { const a = document.createElement("a"); a.href = data.signedUrl; a.download = doc.file_name; a.click(); }
                                }}
                              >
                                <Download className="h-3.5 w-3.5" />
                              </Button>
                              <label className="cursor-pointer" title="Replace">
                                <input
                                  type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png"
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file || !application || !user) return;
                                    const filePath = `${userId}/${application.id}/${Date.now()}_${sanitizeFileName(file.name)}`;
                                    const { error: uploadError } = await supabase.storage.from("application-documents").upload(filePath, file);
                                    if (uploadError) { toast({ title: "Upload Failed", description: uploadError.message, variant: "destructive" }); return; }
                                    await supabase.storage.from("application-documents").remove([doc.file_path]);
                                    await supabase.from("documents").delete().eq("id", doc.id);
                                    const { error: docError } = await supabase.from("documents").insert({ application_id: application.id, user_id: userId, document_type: doc.document_type, file_name: file.name, file_path: filePath });
                                    if (docError) { toast({ title: "Record Failed", description: docError.message, variant: "destructive" }); return; }
                                    toast({ title: "Replaced", description: `${doc.document_type} replaced successfully` });
                                    fetchData();
                                    e.target.value = "";
                                  }}
                                />
                                <span className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-border/60 text-muted-foreground hover:bg-primary hover:text-white hover:border-primary transition-all duration-200 cursor-pointer">
                                  <Upload className="h-3.5 w-3.5" />
                                </span>
                              </label>
                              <Button
                                size="sm" variant="outline" title="Delete"
                                className="h-8 w-8 p-0 border-border/60 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600 transition-all duration-200"
                                onClick={() => setDocToDelete(doc)}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        )) : (
                          <div className="flex flex-col items-center py-8 text-center border rounded-xl bg-muted/10">
                            <FileText className="h-6 w-6 text-muted-foreground/30 mb-1" />
                            <p className="text-xs text-muted-foreground mb-3">No documents uploaded yet</p>
                            <Button size="sm" variant="outline" className="h-8 px-3 text-xs gap-1.5" onClick={() => setShowAddDoc(true)}>
                              <Upload className="h-3 w-3" /> Upload Document
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </>
            ) : (
              <Card className="glass-premium shadow-sm">
                <CardContent className="p-6">
                  <h3 className="text-sm font-bold text-foreground border-l-[3px] border-primary pl-3 mb-6">Submit Application</h3>
                  <div className="space-y-5">
                    {/* Qualification */}
                    <div className="space-y-1.5">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Qualification <span className="text-destructive">*</span></p>
                      <Combobox
                        options={courseOpts}
                        value={applyForm.qualification}
                        onChange={(v) => setApplyForm({ ...applyForm, qualification: v })}
                        placeholder="Select qualification"
                        searchPlaceholder="Search…"
                        emptyText="No courses — add them in Settings."
                      />
                    </div>
                    {/* Country & City */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Country <span className="text-destructive">*</span></p>
                        <Combobox
                          options={loc?.countries ?? []}
                          value={applyForm.country}
                          onChange={(v) => setApplyForm({ ...applyForm, country: v, city: "" })}
                          placeholder={loc ? "Select country" : "Loading…"}
                          searchPlaceholder="Search country…"
                          disabled={!loc}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">City / Town <span className="text-destructive">*</span></p>
                        <Combobox
                          options={applyCityOpts}
                          value={applyForm.city}
                          onChange={(v) => setApplyForm({ ...applyForm, city: v })}
                          placeholder={applyForm.country ? "Select city" : "Select country first"}
                          searchPlaceholder="Search city…"
                          disabled={!applyForm.country}
                          allowCustom
                        />
                      </div>
                    </div>
                    {/* Admission Year & Gender */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Admission Year <span className="text-destructive">*</span></p>
                        <Select value={applyForm.admission_year} onValueChange={(v) => setApplyForm({ ...applyForm, admission_year: v })}>
                          <SelectTrigger className="h-10">
                            <SelectValue placeholder="Select year" />
                          </SelectTrigger>
                          <SelectContent className="max-h-64">
                            {yearOpts.map((y) => (
                              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Gender <span className="text-destructive">*</span></p>
                        <Select value={applyForm.gender} onValueChange={(v) => setApplyForm({ ...applyForm, gender: v })}>
                          <SelectTrigger className="h-10">
                            <SelectValue placeholder="Select gender" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {/* Notes */}
                    <div className="space-y-1.5">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Notes</p>
                      <Textarea value={applyForm.notes} onChange={(e) => setApplyForm({ ...applyForm, notes: e.target.value })} rows={3} placeholder="Additional notes..." className="resize-none" />
                    </div>
                    {/* Documents */}
                    <div className="space-y-5">
                      {DOCUMENT_SECTIONS.map((section) => (
                        <div key={section.title} className="space-y-2">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {section.title}
                            {section.slots.every((s) => s.required) && <span className="text-destructive"> *</span>}
                          </p>
                          {section.slots.map((slot) => {
                            const picked = applyFiles.find((f) => f.slotId === slot.id);
                            return (
                              <div key={slot.id} className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-muted/10 hover:bg-muted/20 transition-colors">
                                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${picked ? "bg-green-100" : "bg-primary/10"}`}>
                                  {picked
                                    ? <CheckCircle className="h-4 w-4 text-green-600" />
                                    : <FileText className="h-4 w-4 text-primary" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-semibold text-foreground">
                                    {slot.label}
                                    {slot.required
                                      ? <span className="text-destructive"> *</span>
                                      : <span className="font-normal text-muted-foreground/60"> (optional)</span>}
                                  </p>
                                  <p className={`text-xs truncate mt-0.5 ${picked ? "text-green-600" : "text-muted-foreground"}`}>
                                    {picked ? picked.file.name : "No file selected"}
                                  </p>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                  <label className="cursor-pointer">
                                    <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png"
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                          setApplyFiles((prev) => [...prev.filter((f) => f.slotId !== slot.id), { file, slotId: slot.id, type: slot.type }]);
                                        }
                                        e.target.value = "";
                                      }}
                                    />
                                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-primary/40 text-primary hover:bg-primary hover:text-white hover:border-primary transition-all duration-200">
                                      <Upload className="h-3 w-3" />
                                      {picked ? "Replace" : "Browse"}
                                    </span>
                                  </label>
                                  {picked && !slot.required && (
                                    <Button
                                      size="sm" variant="ghost" title="Remove"
                                      className="h-8 w-8 p-0 text-muted-foreground hover:text-red-600"
                                      onClick={() => setApplyFiles((prev) => prev.filter((f) => f.slotId !== slot.id))}
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mt-6 pt-5 border-t border-border/40">
                    <Button onClick={handleSubmitApplication} disabled={applyLoading} className="w-full gradient-warm text-white border-0 h-11 text-sm font-semibold">
                      {applyLoading ? "Submitting..." : "Submit Application"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      {/* Delete document confirmation */}
      <AlertDialog open={!!docToDelete} onOpenChange={(open) => { if (!open && !deletingDoc) setDocToDelete(null); }}>
        <AlertDialogContent className="max-w-md rounded-2xl">
          <AlertDialogHeader className="items-center text-center sm:text-center">
            <div className="mb-1 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <AlertDialogTitle>Delete this document?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong className="text-foreground">{docToDelete?.document_type}</strong>
              {docToDelete?.file_name ? <> ({docToDelete.file_name})</> : null} will be permanently removed
              from this application. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:justify-center gap-2">
            <AlertDialogCancel disabled={deletingDoc} className="mt-0 min-w-[110px]">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deletingDoc}
              className="min-w-[110px] bg-red-600 hover:bg-red-700 text-white"
              onClick={(e) => { e.preventDefault(); if (docToDelete) deleteDocument(docToDelete); }}
            >
              {deletingDoc ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default StudentDetail;
